import {
  Card,
  CardContent,
  Typography,
  Grid,
  Autocomplete,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Box,
  Button,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import PropTypes from "prop-types";

import rotDecklists from "../game-form/rotation-decklists";
import unlDecklists from "../game-form/unlimited-decklists";
import { ROTATION } from "../../constants";
import FormatButtons from "../format-buttons/FormatButtons";
import SeasonSelect from "../season-select/SeasonSelect";

const SearchForm = ({
  format,
  season,
  onFormSubmit,
  onFormatChange,
  onSeasonChange,
  onCSVExport
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({ defaultValues: { deck: "", opponent: "" } });

  const resetForm = () => {
    reset({
      deck: "",
      opponent: "",
      order: "",
      result: ""
    });
  };

  const handleFormSubmit = (data) => onFormSubmit(data);
  const handleFormatChange = (formatValue) => onFormatChange(formatValue);
  const handleCSVExport = () => onCSVExport();

  return (
    <Card sx={{ marginBottom: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Search Your Games
        </Typography>

        <Box
          display="flex"
          flexDirection={isSmallScreen ? "column" : "row"}
          justifyContent="space-around"
        >
          <FormatButtons format={format} onFormatClick={handleFormatChange} />
          <SeasonSelect season={season} onSeasonChange={onSeasonChange} />
        </Box>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Grid container spacing={2} mt={0.01}>
            <Grid item xs={12} md={3}>
              <Controller
                name="deck"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={format === ROTATION ? rotDecklists : unlDecklists}
                    freeSolo
                    autoHighlight
                    autoSelect
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Deck"
                        size="small"
                        error={!!errors.deck}
                        helperText={errors.deck && errors.deck.message}
                      />
                    )}
                    onChange={(_, data) => field.onChange(data)}
                  />
                )}
                rules={{
                  maxLength: {
                    value: 50,
                    message:
                      "Please limit the deck name to less than 50 characters."
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Controller
                name="opponent"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={rotDecklists}
                    freeSolo
                    autoHighlight
                    autoSelect
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Opponent"
                        size="small"
                        error={!!errors.opponent}
                        helperText={errors.opponent && errors.opponent.message}
                      />
                    )}
                    onChange={(_, data) => field.onChange(data)}
                  />
                )}
                rules={{
                  maxLength: {
                    value: 50,
                    message:
                      "Please limit your the deck name to less than 50 characters."
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" error={!!errors.order}>
                <InputLabel>Order</InputLabel>
                <Controller
                  control={control}
                  name="order"
                  defaultValue=""
                  render={({ field }) => (
                    <Select label="Order" {...field}>
                      <MenuItem value="1st">1st</MenuItem>
                      <MenuItem value="2nd">2nd</MenuItem>
                    </Select>
                  )}
                />
                {errors.order && (
                  <FormHelperText>{errors.order.message}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small" error={!!errors.result}>
                <InputLabel>Result</InputLabel>
                <Controller
                  control={control}
                  name="result"
                  defaultValue=""
                  render={({ field }) => (
                    <Select label="Result" {...field}>
                      <MenuItem value="WIN">WIN</MenuItem>
                      <MenuItem value="LOSE">LOSE</MenuItem>
                    </Select>
                  )}
                />
                {errors.result && (
                  <FormHelperText>{errors.result.message}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
          <Box
            sx={{
              display: "flex",
              flexDirection: isSmallScreen ? "column-reverse" : "row",
              marginTop: 1
            }}
          >
            <Button
              color="primary"
              onClick={handleCSVExport}
              fullWidth
              sx={{
                marginTop: isSmallScreen ? 1 : 0,
                marginRight: isSmallScreen ? 0 : 1
              }}
            >
              Export to CSV
            </Button>
            <Button
              color="info"
              fullWidth
              sx={{
                marginTop: isSmallScreen ? 1 : 0,
                marginRight: isSmallScreen ? 0 : 1
              }}
              type="reset"
              onClick={resetForm}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{
                marginBottom: isSmallScreen ? 1 : 0,
                marginLeft: isSmallScreen ? 0 : 1
              }}
              type="submit"
            >
              Search
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

SearchForm.propTypes = {
  format: PropTypes.string,
  season: PropTypes.object,
  onFormSubmit: PropTypes.func,
  onSeasonChange: PropTypes.func,
  onFormatChange: PropTypes.func,
  onCSVExport: PropTypes.func
};

export default SearchForm;
